import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { TrialStatusBadge } from '@/modules/monitor/components/TrialStatusBadge';
import {
  PortalCustomerRoleBadge,
  PortalEmptyValue,
  PortalSubscriptionStatusBadge,
  PortalSystemAccessBadge,
  PortalTenantStatusBadge,
} from '@/modules/portal/components/PortalStatusBadges';
import { PortalDetailGrid } from '@/modules/portal/components/ui/PortalDetailGrid';
import { PortalKpiCard } from '@/modules/portal/components/ui/PortalKpiCard';
import { PortalPage } from '@/modules/portal/components/ui/PortalPage';
import { PortalQuickActionCard } from '@/modules/portal/components/ui/PortalQuickActionCard';
import { PortalLoadingState, portalSpinClass } from '@/modules/portal/components/ui/portal-loading';
import { PortalFadeIn, PortalStagger } from '@/modules/portal/components/ui/portal-motion';
import { fetchPortalOverview } from '@/modules/portal/services/portalOverview.api';
import {
  companyDisplayHeading,
  formatPortalCountry,
} from '@/modules/portal/utils/portalCompanyProfileHelpers';
import { formatPortalDate } from '@/modules/portal/utils/portalOverviewHelpers';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';

export function PortalCompanyProfilePage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US';

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['me', 'portal-overview'],
    queryFn: fetchPortalOverview,
  });

  if (isLoading) {
    return (
      <PortalLoadingState
        size="md"
        message={t('common.loading')}
        className="min-h-[280px] justify-center"
      />
    );
  }

  if (error) {
    return (
      <div className="portal-glass-panel mx-auto max-w-lg space-y-4 rounded-2xl border-destructive/30 p-6">
        <h2 className="text-lg font-semibold text-foreground">
          {t('portal.companyProfile.error.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('portal.companyProfile.error.message')}</p>
        <Button type="button" variant="outline" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const hasSubscription = data.subscription_id !== null;
  const heading = companyDisplayHeading(data);
  const countryLabel = formatPortalCountry(data.country_code, locale);

  const companyItems = [
    {
      id: 'legal-name',
      label: t('portal.companyProfile.fields.companyName'),
      value: data.tenant_legal_name?.trim() || <PortalEmptyValue />,
    },
    {
      id: 'display-name',
      label: t('portal.companyProfile.fields.displayName'),
      value: data.tenant_display_name?.trim() ? data.tenant_display_name : <PortalEmptyValue />,
    },
    {
      id: 'billing-email',
      label: t('portal.companyProfile.fields.billingEmail'),
      value: data.billing_email ? (
        <a href={`mailto:${data.billing_email}`} className="text-primary hover:underline">
          {data.billing_email}
        </a>
      ) : (
        <PortalEmptyValue />
      ),
    },
    {
      id: 'country',
      label: t('portal.companyProfile.fields.country'),
      value: countryLabel ?? <PortalEmptyValue />,
    },
    {
      id: 'tenant-status',
      label: t('portal.companyProfile.fields.tenantStatus'),
      value: <PortalTenantStatusBadge status={data.tenant_status} />,
    },
    {
      id: 'created',
      label: t('portal.companyProfile.fields.accountCreated'),
      value: formatPortalDate(data.tenant_created_at, locale),
    },
  ];

  const adminItems = [
    {
      id: 'admin-name',
      label: t('portal.companyProfile.fields.adminName'),
      value: data.customer_name?.trim() || <PortalEmptyValue />,
    },
    {
      id: 'admin-email',
      label: t('portal.companyProfile.fields.adminEmail'),
      value: data.customer_email ? (
        <a href={`mailto:${data.customer_email}`} className="text-primary hover:underline">
          {data.customer_email}
        </a>
      ) : (
        <PortalEmptyValue />
      ),
    },
    {
      id: 'admin-role',
      label: t('portal.companyProfile.fields.adminRole'),
      value: <PortalCustomerRoleBadge role={data.customer_role} />,
    },
  ];

  const subscriptionItems = [
    {
      id: 'product',
      label: t('portal.companyProfile.fields.product'),
      value: data.product_name ?? <PortalEmptyValue />,
    },
    {
      id: 'plan',
      label: t('portal.companyProfile.fields.plan'),
      value: data.plan_name ?? <PortalEmptyValue />,
    },
    {
      id: 'subscription-status',
      label: t('portal.companyProfile.fields.subscriptionStatus'),
      value: <PortalSubscriptionStatusBadge status={data.subscription_status} />,
    },
    {
      id: 'system-access',
      label: t('portal.companyProfile.fields.systemAccess'),
      value: <PortalSystemAccessBadge status={data.system_access_status} />,
    },
    {
      id: 'trial',
      label: t('portal.companyProfile.fields.trialStatus'),
      value: data.trial_status ? (
        <TrialStatusBadge
          trialStatus={data.trial_status}
          subscriptionStatus={data.subscription_status ?? ''}
        />
      ) : (
        <PortalEmptyValue />
      ),
    },
    {
      id: 'entitlement',
      label: t('portal.companyProfile.fields.entitlement'),
      value: data.entitlement_status ? (
        t(`portal.overview.entitlement.${data.entitlement_status}`, {
          defaultValue: data.entitlement_status,
        })
      ) : (
        <PortalEmptyValue />
      ),
    },
  ];

  return (
    <PortalPage
      title={t('portal.pages.companyProfile.title')}
      description={t('portal.companyProfile.description')}
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isFetching}
          onClick={() => void refetch()}
          className="rounded-xl"
        >
          <Icons.RefreshCw className={cn('me-2 h-4 w-4', isFetching && portalSpinClass)} />
          {t('common.refresh')}
        </Button>
      }
    >
      <PortalFadeIn>
        <section className="portal-glass-panel overflow-hidden rounded-2xl">
          <div className="border-b border-border/50 bg-gradient-to-br from-primary/8 via-transparent to-transparent px-5 py-6 sm:px-8 sm:py-8">
            <div className="flex items-start gap-4">
              <div
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/12 text-primary"
                aria-hidden
              >
                <Icons.Building className="h-7 w-7" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('portal.companyProfile.identity.label')}
                </p>
                <h2 className="mt-1 truncate text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                  {heading || <PortalEmptyValue />}
                </h2>
                {data.tenant_legal_name &&
                data.tenant_display_name?.trim() &&
                data.tenant_display_name !== data.tenant_legal_name ? (
                  <p className="mt-1 truncate text-sm text-muted-foreground">
                    {data.tenant_legal_name}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </PortalFadeIn>

      <PortalStagger className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PortalKpiCard
          label={t('portal.companyProfile.cards.tenantStatus')}
          icon={Icons.CheckCircle}
        >
          <PortalTenantStatusBadge status={data.tenant_status} />
        </PortalKpiCard>
        <PortalKpiCard label={t('portal.companyProfile.cards.subscription')} icon={Icons.Repeat}>
          <PortalSubscriptionStatusBadge status={data.subscription_status} />
        </PortalKpiCard>
      </PortalStagger>

      <PortalDetailGrid
        title={t('portal.companyProfile.sections.company')}
        description={t('portal.companyProfile.sections.companyHint')}
        items={companyItems}
      />

      <PortalDetailGrid
        title={t('portal.companyProfile.sections.admin')}
        description={t('portal.companyProfile.sections.adminHint')}
        items={adminItems}
      />

      <PortalDetailGrid
        title={t('portal.companyProfile.sections.subscription')}
        description={t('portal.companyProfile.sections.subscriptionHint')}
        items={subscriptionItems}
        footer={
          !hasSubscription ? (
            <p className="text-sm text-muted-foreground">
              {t('portal.companyProfile.subscriptionEmpty')}
            </p>
          ) : null
        }
      />

      {!hasSubscription ? (
        <div className="portal-empty-state">
          <Icons.Layers className="mx-auto h-10 w-10 text-muted-foreground/50" aria-hidden />
          <p className="mt-4 text-lg font-semibold text-foreground">
            {t('portal.companyProfile.emptySubscription.title')}
          </p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {t('portal.companyProfile.emptySubscription.message')}
          </p>
        </div>
      ) : null}

      <section>
        <h2 className="text-base font-semibold text-foreground">
          {t('portal.companyProfile.quickActions')}
        </h2>
        <PortalStagger className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <PortalQuickActionCard
            to="/portal/subscription"
            label={t('portal.companyProfile.actions.viewSubscription')}
            description={t('portal.companyProfile.actions.viewSubscriptionHint')}
            icon={Icons.Repeat}
          />
          <PortalQuickActionCard
            to="/portal/overview"
            label={t('portal.companyProfile.actions.viewOverview')}
            description={t('portal.companyProfile.actions.viewOverviewHint')}
            icon={Icons.BarChart}
          />
        </PortalStagger>
      </section>

      <p className="text-center text-xs text-muted-foreground">
        {t('portal.companyProfile.readOnlyNote')}
      </p>
    </PortalPage>
  );
}
